import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useModal } from '../context/ModalContext';
import { translations } from '../translations';
import api from '../utils/api';
import { trackLead, trackContact } from '../utils/facebookPixel';
import { pushFormView, pushFormStart, pushSubmitAttempt, pushLead } from '../utils/dataLayer';

const Contact = () => {
  const { language } = useLanguage();
  const { alert } = useModal();
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const formRef = useRef(null);
  const formViewFiredRef = useRef(false);
  const formStartFiredRef = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // GTM/GA4: form_view when form container enters viewport (once per mount)
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !formViewFiredRef.current) {
          formViewFiredRef.current = true;
          pushFormView('contact', { page_path: '/contact' });
        }
      },
      { threshold: 0.2, rootMargin: '0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // GTM/GA4: form_start on first interaction with any form field (once)
  const handleFormStart = () => {
    if (formStartFiredRef.current) return;
    formStartFiredRef.current = true;
    pushFormStart('contact', { page_path: '/contact' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // GTM/GA4: submit_attempt – fired when user clicks submit (even if validation/API fails)
    pushSubmitAttempt('contact', { page_path: '/contact' });
    setLoading(true);
    try {
      await api.post('/messages', formData);
      setSuccess(true);

      // GTM/GA4: lead conversion – ONLY after confirmed server success
      pushLead('contact', { value: 0, currency: 'EGP', page_path: '/contact' });

      // Track Facebook Pixel events on successful submission
      trackContact();
      trackLead({
        content_name: 'Contact Form',
        content_category: 'Contact',
        value: 0,
        currency: 'EGP'
      });
    } catch (error) {
      await alert(
        error.response?.data?.error || (language === 'en' ? 'Error sending message' : 'خطأ في إرسال الرسالة'),
        language === 'en' ? 'Error' : 'خطأ',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const [contactSettings, setContactSettings] = useState(null);

  useEffect(() => {
    const fetchContactSettings = async () => {
      try {
        const res = await api.get('/settings');
        setContactSettings(res.data?.contactInfo);
      } catch (error) {
        console.error('Error fetching contact settings:', error);
      }
    };
    fetchContactSettings();
  }, []);

  const contactMethods = [
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: language === 'en' ? 'Visit Our Office' : 'زر مكتبنا',
      description: language === 'en' ? 'Come meet us in person' : 'تعال وقابلنا شخصياً',
      info: contactSettings?.address?.[language] || (language === 'en' ? 'New Cairo Business District, Cairo, Egypt' : 'حي القاهرة الجديدة للأعمال، القاهرة، مصر'),
      action: language === 'en' ? 'Get Directions' : 'احصل على الاتجاهات',
      color: '#2563eb'
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: language === 'en' ? 'Call Us Directly' : 'اتصل بنا مباشرة',
      description: language === 'en' ? 'We are available 24/7' : 'نحن متاحون على مدار الساعة',
      info: contactSettings?.phone || '+20 100 000 0000',
      action: language === 'en' ? 'Call Now' : 'اتصل الآن',
      color: '#4ade80'
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: language === 'en' ? 'Email Us' : 'راسلنا',
      description: language === 'en' ? 'Get a response within 24h' : 'احصل على رد خلال 24 ساعة',
      info: contactSettings?.email || 'info@limousine.com',
      action: language === 'en' ? 'Send Email' : 'أرسل بريد',
      color: '#60a5fa'
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      title: language === 'en' ? 'Live Chat' : 'دردشة مباشرة',
      description: language === 'en' ? 'Chat with our support team' : 'تحدث مع فريق الدعم',
      info: contactSettings?.liveChatHours?.[language] || (language === 'en' ? 'Available 9am - 11pm' : 'متاح من 9 ص - 11 م'),
      action: language === 'en' ? 'Start Chat' : 'ابدأ الدردشة',
      color: '#f472b6'
    }
  ];

  const faqs = [
    {
      q: language === 'en' ? 'What are your operating hours?' : 'ما هي ساعات العمل؟',
      a: language === 'en'
        ? 'Our limousine service operates 24/7, 365 days a year. You can book a ride at any time, and our customer support team is always available to assist you.'
        : 'خدمة الليموزين لدينا تعمل على مدار الساعة طوال أيام الأسبوع، 365 يومًا في السنة. يمكنك حجز رحلة في أي وقت، وفريق دعم العملاء لدينا متاح دائمًا لمساعدتك.'
    },
    {
      q: language === 'en' ? 'How far in advance should I book?' : 'قبل كم من الوقت يجب أن أحجز؟',
      a: language === 'en'
        ? 'We recommend booking at least 24 hours in advance for standard rides. For special events or airport transfers, booking 48-72 hours ahead ensures availability of your preferred vehicle.'
        : 'نوصي بالحجز قبل 24 ساعة على الأقل للرحلات العادية. للمناسبات الخاصة أو النقل من المطار، الحجز قبل 48-72 ساعة يضمن توفر سيارتك المفضلة.'
    },
    {
      q: language === 'en' ? 'What payment methods do you accept?' : 'ما هي طرق الدفع المقبولة؟',
      a: language === 'en'
        ? 'We accept various payment methods including bank transfers, Vodafone Cash, Orange Money, InstaPay, and cash payments. Corporate accounts are also available for business clients.'
        : 'نقبل طرق دفع متعددة بما في ذلك التحويلات البنكية، فودافون كاش، أورانج موني، إنستاباي، والدفع النقدي. كما تتوفر حسابات الشركات للعملاء من قطاع الأعمال.'
    },
    {
      q: language === 'en' ? 'Can I cancel or modify my booking?' : 'هل يمكنني إلغاء أو تعديل حجزي؟',
      a: language === 'en'
        ? 'Yes, you can cancel or modify your booking up to 4 hours before the scheduled pickup time without any charges. Changes made within 4 hours may incur a small fee.'
        : 'نعم، يمكنك إلغاء أو تعديل حجزك حتى 4 ساعات قبل وقت الاستلام المحدد بدون أي رسوم. التغييرات التي تتم خلال 4 ساعات قد تتضمن رسومًا بسيطة.'
    }
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-blackC flex items-center justify-center px-4">
        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[500px] h-[500px] bg-[#4ade80]/5 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-md w-full text-center relative z-10">
          {/* Success Animation */}
          <div className="relative mb-8">
            <div className="w-28 h-28 mx-auto relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-[#4ade80] animate-pulse" style={{ animationDuration: '2s' }} />
              <div className="absolute inset-1 rounded-full bg-blackC flex items-center justify-center">
                <svg className="w-14 h-14 text-[#4ade80]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            {/* Decorative rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 rounded-full border border-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
            </div>
          </div>

          <h2 className="font-display text-4xl lg:text-5xl text-cream mb-4">
            {language === 'en' ? 'Message Sent!' : 'تم إرسال الرسالة!'}
          </h2>
          <p className="text-gray-silver text-lg mb-8 max-w-sm mx-auto">
            {language === 'en'
              ? 'Thank you for reaching out. Our team will get back to you within 24 hours.'
              : 'شكراً لتواصلك معنا. سيرد عليك فريقنا خلال 24 ساعة.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                setSuccess(false);
                setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
              }}
              className="btn-primary"
            >
              {language === 'en' ? 'Send Another Message' : 'إرسال رسالة أخرى'}
            </button>
            <a href="/" className="btn-secondary">
              {language === 'en' ? 'Back to Home' : 'العودة للرئيسية'}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blackC">
      {/* Hero Section */}
      <section className="relative py-32 lg:py-44 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-dark via-blackC to-blackC" />
          <div className="absolute top-20 left-1/4 w-[800px] h-[800px] bg-primary/3 rounded-full blur-[150px]" />
          <div className="absolute -bottom-40 right-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px]" />

          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(#c9a962 1px, transparent 1px), linear-gradient(90deg, #c9a962 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 px-4 py-2 rounded-full mb-8">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-primary text-sm tracking-wide">
                {language === 'en' ? "We're Here to Help" : 'نحن هنا لمساعدتك'}
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-cream mb-6">
              {language === 'en' ? (
                <>Get in <span className="text-primary">Touch</span></>
              ) : (
                <>تواصل <span className="text-primary">معنا</span></>
              )}
            </h1>

            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-primary" />
              <div className="w-3 h-3 rotate-45 border border-primary" />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-primary" />
            </div>

            <p className="text-gray-silver text-lg lg:text-xl max-w-2xl mx-auto mb-6">
              {language === 'en'
                ? 'Have a question or need assistance? Our dedicated team is ready to provide you with exceptional service.'
                : 'هل لديك سؤال أو تحتاج مساعدة؟ فريقنا المتخصص جاهز لتقديم خدمة استثنائية لك.'}
            </p>

            {/* Quick Actions - WhatsApp & Phone */}
            {contactSettings?.phone && (
              <div className="flex items-center justify-center gap-4 flex-wrap">
                {(() => {
                  const phone = contactSettings.phone;
                  const isEgyptian = phone.startsWith('+20');
                  // Format phone for WhatsApp (remove spaces, dashes, and keep +20)
                  const whatsappNumber = isEgyptian
                    ? phone.replace(/\s+/g, '').replace(/-/g, '')
                    : phone.replace(/\s+/g, '').replace(/-/g, '');

                  return (
                    <>
                      {/* WhatsApp Button */}
                      <a
                        href={`https://wa.me/${whatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#20BA5A] text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-[#25D366]/30 group"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.98 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        <span>{language === 'en' ? 'WhatsApp' : 'واتساب'}</span>
                        <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>

                      {/* Phone Button */}
                      <a
                        href={`tel:${phone.replace(/\s+/g, '')}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-[#d4af37] text-blackC font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/30 group"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{language === 'en' ? 'Call Now' : 'اتصل الآن'}</span>
                        <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </a>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-primary rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 lg:py-28 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactMethods.map((method, idx) => (
              <div
                key={idx}
                className={`group relative bg-dark border border-gray-dark p-8 hover:border-primary/30 transition-all duration-500 overflow-hidden cursor-pointer transform hover:-translate-y-2`}
                style={{
                  transitionDelay: `${idx * 100}ms`,
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(20px)'
                }}
              >
                {/* Hover Gradient */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `linear-gradient(135deg, ${method.color}08 0%, transparent 60%)` }}
                />

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-20 h-20">
                  <div className="absolute top-3 right-3 w-8 h-8 border-t border-r opacity-0 group-hover:opacity-100 transition-all duration-300" style={{ borderColor: method.color }} />
                </div>

                <div className="relative z-10">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${method.color}15`, color: method.color }}
                  >
                    {method.icon}
                  </div>

                  <h3 className="font-display text-xl text-cream mb-2">{method.title}</h3>
                  <p className="text-gray-light text-sm mb-4">{method.description}</p>
                  <p className="text-gray-silver font-medium mb-6" dir="ltr">{method.info}</p>

                  <span
                    className="inline-flex items-center gap-2 text-sm transition-colors"
                    style={{ color: method.color }}
                    onClick={() => {
                      if (method.action === 'Call Now') {
                        window.location.href = `tel:${contactSettings?.phone || '+20 100 000 0000'}`;
                      } else if (method.action === 'Send Email') {
                        window.location.href = `mailto:${contactSettings?.email || 'info@limousine.com'}`;
                      } else if (method.action === 'Start Chat') {
                        window.location.href = '/chat';
                      }
                    }}
                  >
                    {method.action}
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content - Form & FAQ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Form */}
            <div ref={formRef}>
              <div className="mb-10">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-3">
                  {language === 'en' ? 'Send a Message' : 'أرسل رسالة'}
                </p>
                <h2 className="font-display text-3xl lg:text-4xl text-cream mb-4">
                  {language === 'en' ? "Let's Start a Conversation" : 'لنبدأ محادثة'}
                </h2>
                <p className="text-gray-light">
                  {language === 'en'
                    ? 'Fill out the form below and our team will respond promptly.'
                    : 'املأ النموذج أدناه وسيرد فريقنا بسرعة.'}
                </p>
              </div>

              <form onSubmit={handleSubmit} onFocusCapture={handleFormStart} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name Field */}
                  <div className="relative">
                    <label
                      className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'name' || formData.name
                        ? 'text-xs text-primary -top-2.5 left-3 bg-blackC px-1'
                        : 'text-gray-light top-4 left-4'
                        }`}
                    >
                      {t.name} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('name')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full bg-transparent border border-gray-dark text-cream px-4 py-4 focus:outline-none focus:border-primary/50 transition-all duration-300 rounded-lg"
                      required
                    />
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <label
                      className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'email' || formData.email
                        ? 'text-xs text-primary -top-2.5 left-3 bg-blackC px-1'
                        : 'text-gray-light top-4 left-4'
                        }`}
                    >
                      {t.email} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full bg-transparent border border-gray-dark text-cream px-4 py-4 focus:outline-none focus:border-primary/50 transition-all duration-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Phone Field */}
                  <div className="relative">
                    <label
                      className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'phone' || formData.phone
                        ? 'text-xs text-primary -top-2.5 left-3 bg-blackC px-1'
                        : 'text-gray-light top-4 left-4'
                        }`}
                    >
                      {t.phone}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full bg-transparent border border-gray-dark text-cream px-4 py-4 focus:outline-none focus:border-primary/50 transition-all duration-300 rounded-lg"
                      dir="ltr"
                    />
                  </div>

                  {/* Subject Field */}
                  <div className="relative">
                    <label
                      className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'subject' || formData.subject
                        ? 'text-xs text-primary -top-2.5 left-3 bg-blackC px-1'
                        : 'text-gray-light top-4 left-4'
                        }`}
                    >
                      {t.subject}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('subject')}
                      onBlur={() => setFocusedField(null)}
                      className="w-full bg-transparent border border-gray-dark text-cream px-4 py-4 focus:outline-none focus:border-primary/50 transition-all duration-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Message Field */}
                <div className="relative">
                  <label
                    className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'message' || formData.message
                      ? 'text-xs text-primary -top-2.5 left-3 bg-blackC px-1'
                      : 'text-gray-light top-4 left-4'
                      }`}
                  >
                    {t.message} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField(null)}
                    rows="5"
                    className="w-full bg-transparent border border-gray-dark text-cream px-4 py-4 focus:outline-none focus:border-primary/50 transition-all duration-300 rounded-lg resize-none"
                    required
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="relative w-full md:w-auto group overflow-hidden"
                >
                  <div className="relative bg-gradient-to-r from-primary to-tertiary text-blackC font-semibold py-4 px-10 flex items-center justify-center gap-3 rounded-lg transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/20">
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                        {language === 'en' ? 'Sending...' : 'جاري الإرسال...'}
                      </>
                    ) : (
                      <>
                        {t.send}
                        <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-white/20 translate-x-[-100%] skew-x-12 group-hover:translate-x-[100%] transition-transform duration-700" />
                </button>
              </form>
            </div>

            {/* FAQ Section */}
            <div>
              <div className="mb-10">
                <p className="text-primary text-sm tracking-[0.3em] uppercase mb-3">
                  {language === 'en' ? 'FAQ' : 'الأسئلة الشائعة'}
                </p>
                <h2 className="font-display text-3xl lg:text-4xl text-cream mb-4">
                  {language === 'en' ? 'Frequently Asked Questions' : 'الأسئلة المتكررة'}
                </h2>
                <p className="text-gray-light">
                  {language === 'en'
                    ? 'Find quick answers to common questions about our services.'
                    : 'اعثر على إجابات سريعة للأسئلة الشائعة حول خدماتنا.'}
                </p>
              </div>

              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className={`border border-gray-dark rounded-lg overflow-hidden transition-all duration-300 ${activeAccordion === idx ? 'bg-dark border-primary/30' : 'bg-transparent hover:border-gray'
                      }`}
                  >
                    <button
                      onClick={() => setActiveAccordion(activeAccordion === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-6 text-left"
                    >
                      <span className={`font-medium transition-colors ${activeAccordion === idx ? 'text-primary' : 'text-cream'}`}>
                        {faq.q}
                      </span>
                      <div className={`w-8 h-8 rounded-full border flex items-center justify-center flex-shrink-0 ml-4 transition-all duration-300 ${activeAccordion === idx ? 'border-primary bg-primary/10 rotate-180' : 'border-gray'
                        }`}>
                        <svg className={`w-4 h-4 transition-colors ${activeAccordion === idx ? 'text-primary' : 'text-gray-light'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${activeAccordion === idx ? 'max-h-48' : 'max-h-0'}`}>
                      <p className="px-6 pb-6 text-gray-silver leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Still have questions? */}
              <div className="mt-10 p-6 bg-dark border border-gray-dark rounded-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-display text-lg text-cream mb-2">
                      {language === 'en' ? 'Still have questions?' : 'لديك المزيد من الأسئلة؟'}
                    </h4>
                    <p className="text-gray-light text-sm mb-4">
                      {language === 'en'
                        ? "Can't find the answer you're looking for? Contact our support team."
                        : 'لم تجد الإجابة التي تبحث عنها؟ تواصل مع فريق الدعم.'}
                    </p>
                    <a href={`tel:${contactSettings?.phone || '+201000000000'}`} className="inline-flex items-center gap-2 text-primary hover:text-[#d4af37] transition-colors text-sm">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {language === 'en' ? `Call ${contactSettings?.phone || '+20 100 000 0000'}` : `اتصل ${contactSettings?.phone || '+20 100 000 0000'}`}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 lg:py-28 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Decorative Frame */}
            <div className="absolute -top-6 -left-6 w-32 h-32 border-t-2 border-l-2 border-primary/30" />
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-b-2 border-r-2 border-primary/30" />

            <div className="relative bg-dark border border-gray-dark p-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Map Visual */}
                <div className="relative aspect-square lg:aspect-auto lg:h-[400px] bg-charcoal rounded-lg overflow-hidden">
                  {/* Stylized Map Background */}
                  <div className="absolute inset-0">
                    <svg className="w-full h-full opacity-20" viewBox="0 0 400 400">
                      {/* Grid lines */}
                      {[...Array(20)].map((_, i) => (
                        <React.Fragment key={i}>
                          <line x1={i * 20} y1="0" x2={i * 20} y2="400" stroke="#c9a962" strokeWidth="0.5" />
                          <line x1="0" y1={i * 20} x2="400" y2={i * 20} stroke="#c9a962" strokeWidth="0.5" />
                        </React.Fragment>
                      ))}
                      {/* Random "roads" */}
                      <path d="M0 150 Q100 150 150 200 T300 250 T400 200" stroke="#c9a962" strokeWidth="2" fill="none" opacity="0.4" />
                      <path d="M100 0 Q100 100 200 150 T200 400" stroke="#c9a962" strokeWidth="2" fill="none" opacity="0.4" />
                      <path d="M300 0 Q300 100 250 200 T350 400" stroke="#c9a962" strokeWidth="1.5" fill="none" opacity="0.3" />
                    </svg>
                  </div>

                  {/* Location Pin */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="relative">
                      <div className="absolute inset-0 bg-primary/30 rounded-full animate-ping" style={{ width: '80px', height: '80px', marginLeft: '-20px', marginTop: '-20px' }} />
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center relative z-10 shadow-lg shadow-primary/30">
                        <svg className="w-5 h-5 text-blackC" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Info */}
                <div>
                  <p className="text-primary text-sm tracking-[0.3em] uppercase mb-4">
                    {language === 'en' ? 'Our Location' : 'موقعنا'}
                  </p>
                  <h3 className="font-display text-3xl text-cream mb-6">
                    {language === 'en' ? 'Visit Our Headquarters' : 'زر مقرنا الرئيسي'}
                  </h3>
                  <p className="text-gray-silver mb-8 leading-relaxed">
                    {language === 'en'
                      ? 'Our main office is located in the heart of New Cairo Business District. Stop by to meet our team, explore our fleet, or discuss your transportation needs in person.'
                      : 'يقع مكتبنا الرئيسي في قلب حي القاهرة الجديدة للأعمال. تفضل بزيارتنا للقاء فريقنا، واستكشاف أسطولنا، أو مناقشة احتياجات النقل الخاصة بك شخصياً.'}
                  </p>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-cream">{contactSettings?.address?.[language] || (language === 'en' ? 'New Cairo Business District' : 'حي القاهرة الجديدة للأعمال')}</p>
                        <p className="text-gray-light text-sm">{language === 'en' ? 'Cairo, Egypt' : 'القاهرة، مصر'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-cream">{language === 'en' ? '24/7 Service Available' : 'خدمة متوفرة على مدار الساعة'}</p>
                        <p className="text-gray-light text-sm">{language === 'en' ? 'Always at your service' : 'دائماً في خدمتكم'}</p>
                      </div>
                    </div>
                  </div>

                  <a
                    href={contactSettings?.googleMapsUrl || 'https://maps.google.com'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 text-primary hover:text-[#d4af37] transition-colors group"
                  >
                    <span>{language === 'en' ? 'Open in Google Maps' : 'افتح في خرائط جوجل'}</span>
                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blackC via-dark to-blackC" />
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px]" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-5xl text-cream mb-6">
            {language === 'en' ? 'Ready to Experience Luxury?' : 'مستعد لتجربة الفخامة؟'}
          </h2>
          <p className="text-gray-silver text-lg mb-10 max-w-2xl mx-auto">
            {language === 'en'
              ? 'Book your premium limousine service today and travel in style with our professional chauffeurs.'
              : 'احجز خدمة الليموزين الفاخرة اليوم وسافر بأناقة مع سائقينا المحترفين.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book" className="btn-primary">
              {t.bookNow}
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
            <a href="/cars" className="btn-secondary">
              {language === 'en' ? 'View Our Fleet' : 'عرض أسطولنا'}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;